import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import FileDropzone from "@/components/FileDropzone";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const PREFS_KEY = "ppc-optimiser-prefs";

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export default function Step1Upload({ apiBaseUrl, onAnalysed, onError }) {
  const initial = loadPrefs();

  const [bulkFile, setBulkFile] = useState(null);
  const [strFile, setStrFile] = useState(null);
  const [aprFile, setAprFile] = useState(null);
  const [targetAcos, setTargetAcos] = useState(initial.targetAcos ?? "");
  const [marginPct, setMarginPct] = useState(initial.marginPct ?? "");
  const [brandName, setBrandName] = useState(initial.brandName ?? "");
  const [runNegatives, setRunNegatives] = useState(initial.runNegatives ?? true);
  const [runHarvesting, setRunHarvesting] = useState(initial.runHarvesting ?? true);
  const [runBidAdjustments, setRunBidAdjustments] = useState(initial.runBidAdjustments ?? true);
  const [higherSpendCap, setHigherSpendCap] = useState(initial.higherSpendCap ?? false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Persist preferences to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        PREFS_KEY,
        JSON.stringify({
          targetAcos,
          marginPct,
          brandName,
          runNegatives,
          runHarvesting,
          runBidAdjustments,
          higherSpendCap,
        })
      );
    } catch {
      // ignore quota / private-mode errors
    }
  }, [targetAcos, marginPct, brandName, runNegatives, runHarvesting, runBidAdjustments, higherSpendCap]);

  const canSubmit = bulkFile && strFile && aprFile && targetAcos !== "" && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onError("");
    setSubmitting(true);

    const form = new FormData();
    form.append("bulk_file", bulkFile);
    form.append("str_file", strFile);
    form.append("apr_file", aprFile);
    form.append("target_acos", targetAcos);
    if (marginPct !== "") form.append("margin_pct", marginPct);
    form.append("brand_name", brandName);
    form.append("run_negatives", runNegatives ? "true" : "false");
    form.append("run_harvesting", runHarvesting ? "true" : "false");
    form.append("run_bid_adjustments", runBidAdjustments ? "true" : "false");
    form.append("higher_spend_cap", runNegatives && higherSpendCap ? "true" : "false");

    try {
      const res = await axios.post(`${apiBaseUrl}/process`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      onAnalysed(res.data);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Analysis failed. Please check your files and try again.";
      onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitting) {
    return (
      <div
        className="terminal border-l-4 border-l-[#00c389] p-12 sm:p-14 flex flex-col items-center justify-center gap-6 shadow-hard-sm"
        data-testid="loading-state"
      >
        <div className="seg-loader" aria-hidden="true">
          <span /><span /><span /><span /><span />
        </div>
        <div className="font-brand-mono text-base uppercase tracking-[2px] text-[#00c389]">
          Analysing your account…
        </div>
        <div className="font-brand-mono text-xs text-[#96a09a] text-center max-w-md leading-relaxed uppercase tracking-wider">
          Parsing your bulk file, search term and advertised product reports. This may take up to a minute.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="step1-form">
      <section className="box shadow-hard p-6 sm:p-9">
        <p className="kicker mb-3">Required Files</p>
        <h2 className="font-['Archivo'] font-extrabold text-2xl sm:text-3xl text-[#0d0d0d] mb-1">Upload Reports</h2>
        <p className="font-brand-mono text-[11px] uppercase tracking-[1.5px] text-[#96a09a] mb-7">
          All three files are required · .xlsx only
        </p>

        <div className="space-y-6">
          <FileDropzone
            id="bulk-file"
            label="Bulk File Export"
            helperText="Download from Seller Central → Campaign Manager → Bulk Operations"
            file={bulkFile}
            onFile={setBulkFile}
          />
          <FileDropzone
            id="str-file"
            label="Search Term Report"
            helperText="Last 30 days, Sponsored Products"
            file={strFile}
            onFile={setStrFile}
          />
          <FileDropzone
            id="apr-file"
            label="Advertised Product Report"
            helperText="Last 30 days, Sponsored Products"
            file={aprFile}
            onFile={setAprFile}
          />
        </div>
      </section>

      <section className="box p-6 sm:p-9">
        <p className="kicker mb-3">Performance Goals</p>
        <h2 className="font-['Archivo'] font-extrabold text-2xl sm:text-3xl text-[#0d0d0d] mb-7">Targets</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="target-acos" className="label-mono mb-2">
              Target ACoS %
            </label>
            <input
              id="target-acos"
              type="number"
              min="0"
              max="100"
              step="0.1"
              required
              value={targetAcos}
              onChange={(e) => setTargetAcos(e.target.value)}
              placeholder="e.g. 30"
              className="input-sharp font-brand-mono"
              data-testid="input-target-acos"
            />
          </div>
          <div>
            <label htmlFor="margin-pct" className="label-mono mb-2">
              Margin %
            </label>
            <input
              id="margin-pct"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={marginPct}
              onChange={(e) => setMarginPct(e.target.value)}
              placeholder="Leave blank to use default (35%)"
              className="input-sharp font-brand-mono"
              data-testid="input-margin-pct"
            />
          </div>
        </div>
      </section>

      <section
        className="box overflow-hidden"
        data-testid="options-section"
      >
        <button
          type="button"
          onClick={() => setAdvancedOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-4 px-6 sm:px-9 py-5 text-left hover:bg-[#f2f3ef] transition-colors"
          aria-expanded={advancedOpen}
          data-testid="advanced-options-toggle"
        >
          <div className="flex items-center gap-3 min-w-0">
            {advancedOpen ? (
              <ChevronDown className="w-4 h-4 text-[#0d0d0d] shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-[#0d0d0d] shrink-0" />
            )}
            <h2 className="font-['Archivo'] font-extrabold text-lg sm:text-xl text-[#0d0d0d]">Advanced Options</h2>
          </div>
          <span className="font-brand-mono text-[10px] uppercase tracking-[1.5px] text-[#96a09a]">
            {advancedOpen ? "Collapse" : "Brand exclusions · run toggles"}
          </span>
        </button>

        {advancedOpen && (
          <div className="px-6 sm:px-9 pb-8 pt-2 border-t-2 border-[#0d0d0d] animate-slide-down" data-testid="advanced-options-content">
            {/* Brand Name */}
            <div className="mb-7 mt-5">
              <label htmlFor="brand-name" className="label-mono mb-2">
                Brand Name
              </label>
              <input
                id="brand-name"
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="e.g. ufit, grenade, huel (optional — comma-separated)"
                className="input-sharp"
                data-testid="input-brand-name"
              />
              <p className="text-xs text-[#565b57] mt-2">
                Separate multiple brand terms with commas. Brand terms will be excluded from exact match harvesting.
              </p>
            </div>

            {/* Toggles */}
            <div className="space-y-4 pt-2 border-t border-[#0d0d0d]/15">
              {/* Run negative keywords */}
              <div className="pt-4">
                <label className="flex items-center justify-between gap-4 cursor-pointer" data-testid="toggle-run-negatives-wrapper">
                  <span className="text-sm font-medium text-[#0d0d0d]">Run negative keywords</span>
                  <Switch
                    checked={runNegatives}
                    onCheckedChange={setRunNegatives}
                    data-testid="toggle-run-negatives"
                  />
                </label>

                {runNegatives && (
                  <div className="mt-3 ml-0 sm:ml-4 pl-4 border-l-2 border-[#0d0d0d]/15" data-testid="aggressive-negation-wrapper">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={higherSpendCap}
                        onCheckedChange={(v) => setHigherSpendCap(v === true)}
                        className="mt-0.5 border-2 border-[#0d0d0d] data-[state=checked]:bg-[#007d62] data-[state=checked]:border-[#007d62]"
                        data-testid="checkbox-higher-spend-cap"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-[#0d0d0d] block">
                          Aggressive negation — increase spend cap from 10% to 20%
                        </span>
                        <span className="text-xs text-[#565b57] block mt-0.5 leading-relaxed">
                          Negates more wasted spend. Worst offenders are always actioned first.
                        </span>
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Run exact match harvesting */}
              <label className="flex items-center justify-between gap-4 cursor-pointer pt-2" data-testid="toggle-run-harvesting-wrapper">
                <span className="text-sm font-medium text-[#0d0d0d]">Run exact match harvesting</span>
                <Switch
                  checked={runHarvesting}
                  onCheckedChange={setRunHarvesting}
                  data-testid="toggle-run-harvesting"
                />
              </label>

              {/* Run bid adjustments */}
              <label className="flex items-center justify-between gap-4 cursor-pointer pt-2" data-testid="toggle-run-bid-adjustments-wrapper">
                <span className="text-sm font-medium text-[#0d0d0d]">Run bid adjustments</span>
                <Switch
                  checked={runBidAdjustments}
                  onCheckedChange={setRunBidAdjustments}
                  data-testid="toggle-run-bid-adjustments"
                />
              </label>
            </div>
          </div>
        )}
      </section>

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="btn-primary"
          data-testid="analyse-account-btn"
        >
          Analyse Account
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
